import { Cookies } from 'react-cookie';
import _ from 'lodash';
import moment from 'moment';
import store from '../redux/store';
import {
  resetPendingOrder,
  showAddressModal,
  updateClosestShops,
  updateCustomerAddressCheck,
} from '../redux/actions';

import { getFirebaseCustomerId } from '../api/firebase/firebaseAuthentication';
import { getGeocodeByAddress, getGeocodeByPlaceId } from '../api/backend';
import {
  COOKIE_ADDRESS,
  COOKIE_ORDER_TYPE,
  COOKIE_SHOP_ID,
  ORDER_TYPE_DELIVERY,
} from '../constants';
import { getShopData } from './shop';
import { CUSTOMER_ADDRESS_CHECK_VALUES } from '../redux/reducers/componentReducer';
import {
  getAddressDetails,
  getAddressFormatted,
  getDistance,
  parseAddress,
} from './map';
import { UPDATE_PENDING_ORDER } from '../redux/actions/types';
import { updateUserShopData } from '../api/users';
import {
  createPendingOrder,
  deletePendingOrder,
  getPendingOrder,
} from '../api/pendingOrder';
import { getDeliverableShopsFromAddress } from '../api/delivery';
import { isObjectNotNull } from './dataType';
import { handleJoiSchemaError } from './error';
import { logLowError, logMediumError } from './logger';

export const isCustomerAddressDefined = (customerAddress: any) => {
  const ignoreProperties = ['instruction', 'street2'];
  for (const key in customerAddress) {
    const isOtherPropertyThanIgnoredOnes = ignoreProperties.indexOf(key) === -1;
    const isDefined = customerAddress[key] != null;
    if (isOtherPropertyThanIgnoredOnes && isDefined) {
      return true;
    }
  }
  return false;
};

export const getCustomerAddress = async (
  customerId: string,
  withShopId = false
): Promise<any> => {
  const pendingOrder =
    store.getState().pendingOrderReducer || (await getPendingOrder(customerId));
  const customerAddress = pendingOrder?.address;
  return withShopId
    ? { customerAddress, shopId: pendingOrder?.shopId }
    : customerAddress;
};

export const generateCustomerAddress = (address: any): any => {
  const newAddress = _.cloneDeep(address);
  const { latitude, longitude } = newAddress.location;
  newAddress.id = `${parseFloat(latitude).toFixed(6)}${parseFloat(
    longitude
  ).toFixed(6)}`.replace(/\./g, '');
  newAddress.selected = true;
  return newAddress;
};

export const removeCustomerPendingOrder = async (): Promise<void> => {
  const cookies = new Cookies();
  cookies.remove(COOKIE_ADDRESS);
  cookies.remove(COOKIE_SHOP_ID);
  cookies.remove(COOKIE_ORDER_TYPE);
  const customerId = getFirebaseCustomerId();
  if (customerId) {
    logLowError(`Removing pendingOrder of customer ${customerId}`);
    await deletePendingOrder(customerId);
  }
  store.dispatch(resetPendingOrder());
};

export const setUserShopIds = async (shopId: string, customerId: string) => {
  // Add shops to firebase
  try {
    const shopIds = store.getState()?.userReducer?.user?.shopIds;
    if (customerId && shopId) {
      const foundShopId = _.find(shopIds, (shopIdTemp) => {
        return shopIdTemp.shopId === shopId;
      });
      if (!foundShopId) {
        const shopIdData = {
          shopId,
          creationTime: moment().format(),
        };
        await updateUserShopData(customerId, shopId, shopIdData);
      }
    }
  } catch (error) {
    logLowError(`Error setting user shop ids ${error}`);
  }
};

export const setUserPendingOrderAddressAndOrderTypeFirebase = async (
  customerId: string,
  shopId: string,
  address: any,
  orderType: string
): Promise<string> => {
  try {
    const { isUserAnonymous } = store.getState().userReducer as any;
    let message = '';
    if (shopId && address && orderType) {
      const response = await createPendingOrder(customerId, {
        address,
        shopId,
        orderType,
      });
      message = response.message || '';
    }
    if (!isUserAnonymous && shopId) {
      await setUserShopIds(shopId, customerId);
    }
    return message;
  } catch (error) {
    logMediumError(
      `Eerror setting user pending order ${handleJoiSchemaError(error)}`
    );
    throw error;
  }
};

export const updateAddressAndShop = async (
  shopId: string,
  newAddress: any,
  orderType: string
): Promise<string> => {
  try {
    const { user } = store.getState().userReducer;
    const { customerId } = user;
    if (newAddress) {
      newAddress = generateCustomerAddress(newAddress);
    }
    return await setUserPendingOrderAddressAndOrderTypeFirebase(
      customerId,
      shopId,
      newAddress,
      orderType
    );
  } catch (error) {
    logMediumError(
      `Error updating address and shop ${handleJoiSchemaError(error)}`
    );
    throw error;
  }
};
export const getDeliverableShops = async (
  address: any,
  handleCatchError?: any
): Promise<any[]> => {
  try {
    const deliverableShops = await getDeliverableShopsFromAddress(address);

    const { shops } = store.getState().shopReducer;

    return _.orderBy(
      _.map(deliverableShops, (deliverableShop) => {
        const shop = shops[deliverableShop.shopId];

        if (!isObjectNotNull(shop)) {
          return null;
        }

        return {
          ...getShopData(shop, deliverableShop.zoneData),
          shopId: deliverableShop.shopId,
          distance: getDistance(
            address.location.latitude,
            address.location.longitude,
            shop.address.location.latitude,
            shop.address.location.longitude
          ),
        };
      }),
      'distance'
    );
  } catch (error) {
    if (handleCatchError) {
      await handleCatchError(error);
    }
    return [];
  }
};

export const checkIfCustomerAddressValid = async (
  customerAddress: any,
  orderType: string,
  shopId: string
) => {
  const customerAddressCheck =
    store.getState().componentReducer?.customerAddressCheck;
  const shopIsLoaded = (store.getState().shopReducer as any)?.shopIsLoaded;
  if (
    customerAddressCheck === CUSTOMER_ADDRESS_CHECK_VALUES.VALID ||
    customerAddressCheck === CUSTOMER_ADDRESS_CHECK_VALUES.PENDING ||
    !shopIsLoaded ||
    !orderType
  ) {
    return;
  }
  if (!isCustomerAddressDefined(customerAddress)) {
    store.dispatch(
      updateCustomerAddressCheck(CUSTOMER_ADDRESS_CHECK_VALUES.VALID)
    );
    return;
  }
  if (orderType !== ORDER_TYPE_DELIVERY) {
    store.dispatch(
      updateCustomerAddressCheck(CUSTOMER_ADDRESS_CHECK_VALUES.VALID)
    );
    return;
  }
  store.dispatch(
    updateCustomerAddressCheck(CUSTOMER_ADDRESS_CHECK_VALUES.PENDING)
  );

  const foundPlaces = await (customerAddress?.placeId
    ? getGeocodeByPlaceId(customerAddress.placeId)
    : getGeocodeByAddress(getAddressFormatted(customerAddress)));
  if (!foundPlaces?.length) {
    store.dispatch(
      updateCustomerAddressCheck(CUSTOMER_ADDRESS_CHECK_VALUES.INVALID)
    );
    store.dispatch(showAddressModal(true));
    return;
  }
  const firstFoundPlace = foundPlaces[0];
  const foundAddress = parseAddress(firstFoundPlace?.address_components);
  const { lat, lng } = firstFoundPlace.geometry.location;
  const { district, subLocality } = getAddressDetails([firstFoundPlace]);
  foundAddress.district = district;
  foundAddress.subLocality = subLocality;
  foundAddress.location = {
    latitude: lat,
    longitude: lng,
  };
  foundAddress.placeId = firstFoundPlace?.place_id;
  const deliverableShops = await getDeliverableShops(foundAddress);
  store.dispatch(updateClosestShops(deliverableShops));
  if (!deliverableShops?.length) {
    store.dispatch(
      updateCustomerAddressCheck(CUSTOMER_ADDRESS_CHECK_VALUES.INVALID)
    );
    store.dispatch(showAddressModal(true));
  } else {
    store.dispatch(
      updateCustomerAddressCheck(CUSTOMER_ADDRESS_CHECK_VALUES.VALID)
    );
    const isDeliverableShopsHaveCurrentShopId = !!deliverableShops.find(
      (shop) => shop?.shopId === shopId
    );
    if (!customerAddress?.placeId) {
      customerAddress.placeId = foundAddress.placeId;
    }
    const pendingOrderShopId = isDeliverableShopsHaveCurrentShopId
      ? shopId
      : deliverableShops[0]?.shopId;
    store.dispatch({
      type: UPDATE_PENDING_ORDER,
      payload: { shopId: pendingOrderShopId, address: customerAddress },
    });
  }
};
